// frontend/src/admin/pages/AdminUsersPage.jsx
import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";

import {
  disableUser,
  enableUser,
  forcePasswordReset,
  getUserDetail,
  listUsers,
} from "../api";

function fmt(iso) {
  return iso ? new Date(iso).toLocaleString() : "—";
}

/** Friendly labels for the consent-ledger document types. */
const CONSENT_LABELS = {
  tos: "Terms of Service",
  privacy_policy: "Privacy Policy",
  dpa: "Data Processing Addendum",
  cookie_policy: "Cookie Policy",
};

/**
 * AdminUsersPage — superadmin user console: search/filter users, inspect a
 * user's org memberships and recent audit trail, and disable / re-enable an
 * account or trigger a password-reset email.
 */
function AdminUsersPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [activeOnly, setActiveOnly] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = { per_page: 100 };
      if (q.trim()) params.q = q.trim();
      if (activeOnly) params.is_active = true;
      const data = await listUsers(params);
      setRows(data.items ?? []);
    } catch {
      toast.error("Failed to load users.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeOnly]);

  const openUser = async (id) => {
    setSelectedId(id);
    setDetail(null);
    try {
      setDetail(await getUserDetail(id));
    } catch {
      toast.error("Failed to load the user.");
    }
  };

  const refreshAfterAction = async (id) => {
    await load();
    if (id === selectedId) await openUser(id);
  };

  const onToggleActive = async () => {
    if (!detail) return;
    setBusy(true);
    try {
      if (detail.is_active) {
        await disableUser(detail.id);
        toast.success("User disabled.");
      } else {
        await enableUser(detail.id);
        toast.success("User re-enabled.");
      }
      await refreshAfterAction(detail.id);
    } catch {
      toast.error("Action failed.");
    } finally {
      setBusy(false);
    }
  };

  const onResetPassword = async () => {
    if (!detail) return;
    setBusy(true);
    try {
      await forcePasswordReset(detail.id, true);
      toast.success("Password-reset email sent.");
    } catch {
      toast.error("Failed to send reset email.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4 max-w-6xl">
      <div>
        <h1 className="text-xl font-bold text-slate-800">Users</h1>
        <p className="mt-1 text-sm text-slate-500">
          Search and inspect accounts — memberships, recent activity, and policy
          acceptances. Select a user to open the detail panel.
        </p>
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          load();
        }}
        className="flex flex-wrap items-center gap-3"
      >
        <input
          aria-label="Search by email"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by email…"
          className="rounded-md border-slate-300 text-sm px-3 py-1.5"
        />
        <button
          type="submit"
          className="bg-slate-800 hover:bg-slate-900 text-white text-sm font-medium rounded-md px-4 py-1.5"
        >
          Search
        </button>
        <label className="flex items-center gap-2 text-sm text-slate-600">
          <input
            type="checkbox"
            aria-label="Active only"
            checked={activeOnly}
            onChange={(e) => setActiveOnly(e.target.checked)}
          />
          Active only
        </label>
        <span className="text-xs text-slate-400">{rows.length} user(s)</span>
      </form>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
        {/* User list */}
        <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-slate-500 border-b border-slate-200 bg-slate-50">
                <th className="px-4 py-2 font-medium">Email</th>
                <th className="px-4 py-2 font-medium">Orgs</th>
                <th className="px-4 py-2 font-medium">Status</th>
                <th className="px-4 py-2 font-medium">Joined</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr>
                  <td colSpan={4} className="px-4 py-6 text-center text-slate-400">
                    Loading…
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-4 py-6 text-center text-slate-400">
                    No users found.
                  </td>
                </tr>
              ) : (
                rows.map((u) => (
                  <tr
                    key={u.id}
                    onClick={() => openUser(u.id)}
                    className={[
                      "cursor-pointer hover:bg-slate-50",
                      selectedId === u.id ? "bg-indigo-50" : "",
                    ].join(" ")}
                  >
                    <td className="px-4 py-2 font-medium text-slate-800">
                      {u.email}
                      {u.is_superadmin && (
                        <span className="ml-2 text-[10px] font-semibold text-amber-600">
                          ADMIN
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-slate-500">{u.org_count}</td>
                    <td className="px-4 py-2">
                      {u.is_active ? (
                        <span className="text-xs text-emerald-600">Active</span>
                      ) : (
                        <span className="text-xs text-rose-600">Disabled</span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-slate-500 whitespace-nowrap">
                      {fmt(u.created_at)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Detail panel (side container) */}
        <div className="bg-white border border-slate-200 rounded-lg p-5 lg:sticky lg:top-4">
          {!selectedId ? (
            <p className="text-sm text-slate-500">Select a user to inspect.</p>
          ) : !detail ? (
            <p className="text-sm text-slate-500">Loading user…</p>
          ) : (
            <>
              <div className="mb-4">
                <div className="text-base font-semibold text-slate-900">
                  {detail.email}
                </div>
                <div className="text-xs text-slate-500">
                  {detail.display_name || "—"} · joined {fmt(detail.created_at)} ·{" "}
                  {detail.is_active ? "Active" : "Disabled"}
                </div>
              </div>

              <div className="flex gap-2 mb-5">
                <button
                  type="button"
                  onClick={onToggleActive}
                  disabled={busy}
                  className={[
                    "text-sm font-medium rounded-md px-3 py-1.5 text-white disabled:opacity-50",
                    detail.is_active
                      ? "bg-rose-600 hover:bg-rose-700"
                      : "bg-emerald-600 hover:bg-emerald-700",
                  ].join(" ")}
                >
                  {detail.is_active ? "Disable" : "Re-enable"}
                </button>
                <button
                  type="button"
                  onClick={onResetPassword}
                  disabled={busy}
                  className="text-sm font-medium rounded-md px-3 py-1.5 border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                >
                  Send password reset
                </button>
              </div>

              <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
                Memberships
              </h3>
              {detail.memberships.length === 0 ? (
                <p className="text-sm text-slate-400 mb-4">None.</p>
              ) : (
                <ul className="text-sm mb-4 space-y-1">
                  {detail.memberships.map((m) => (
                    <li key={m.org_id} className="text-slate-700">
                      {m.org_name}{" "}
                      <span className="text-slate-400">({m.role})</span>
                    </li>
                  ))}
                </ul>
              )}

              <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
                Recent activity
              </h3>
              {detail.recent_audit.length === 0 ? (
                <p className="text-sm text-slate-400">No recent activity.</p>
              ) : (
                <ul className="text-xs space-y-1">
                  {detail.recent_audit.map((a, i) => (
                    <li key={i} className="text-slate-500">
                      <span className="text-slate-700">{a.action}</span> ·{" "}
                      {fmt(a.created_at)}
                    </li>
                  ))}
                </ul>
              )}

              <h3 className="mt-4 text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
                Policy acceptances
              </h3>
              {!detail.consents || detail.consents.length === 0 ? (
                <p className="text-sm text-slate-400">No recorded acceptances.</p>
              ) : (
                <ul className="text-xs space-y-1">
                  {detail.consents.map((c, i) => (
                    <li key={i} className="text-slate-500">
                      <span className="text-slate-700">
                        {CONSENT_LABELS[c.document_type] || c.document_type}
                      </span>{" "}
                      v{c.document_version} · {fmt(c.accepted_at)}
                      {c.ip_subnet ? ` · ${c.ip_subnet}` : ""}
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default AdminUsersPage;
